import { RouterRootState } from 'connected-react-router';
import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import Description from "../../../components/description/Description";
import { useDialog } from '../../../components/dialog/DialogContext';
import { HolidayDetails } from "../../../components/holiday/HolidayDetails";
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { Holiday as HolidayModel } from '../../../models/Holiday';
import { TabSummary } from '../../../models/TabSummary';
import { getHolidayById, getHolidayNamesWithPagination, storeHoliday } from "../../../services/holidaysService";
import { getSiteNames } from "../../../services/siteService";
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { MultiSelectOption } from '../../../models/MultiSelectOption';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem: HolidayModel = {
    name: '',
    startDate: undefined,
    endDate: undefined,
    id: 0,
    site: '',
    siteId: '',
    description: ''
}

const allItemsPath = '/access-control/configuration/holidays';
const itemPath = '/access-control/configuration/holiday/';

const Zone: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<HolidayModel>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [refreshKey, setRefreshKey] = useState(0);


    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage()
    }, [itemId, refreshKey]);



    const reloadPage = useCallback(async () => {
        if (!itemId) {
            setCurrentItem(initialItem);
            return;
        }

        try {
            setBusy(true);
            const holiday = await getHolidayById(itemId);
            holiday.startDate = new Date(holiday.startDate);
            holiday.endDate = new Date(holiday.endDate);

            setSelectedItem({label: holiday.name, value: holiday.id.toString()});
            setCurrentItem(holiday);
            setTabs(initialTabs);

            setBreadcrumb([
                { value: '/access-control/configuration/', displayName: translate.byKey(TranslationKeys.configuration), disableClick: true },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.holidays) },
                { value: itemPath + `${itemId}`, displayName: holiday.name }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        } finally {
            setBusy(false);
        }
    },[itemId, translate, dialog, history])

    const topic: Topic = {
        accessor: 'holName',
        title: translate.byKey(TranslationKeys.holidays),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }];

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const saveHoliday = (values) => {
        const newHoliday: HolidayModel = {
            ...currentItem,
            endDate: values.endDate,
            startDate: values.startDate,
            name: values.name,
            id: +itemId,
            siteId: itemId ? values.siteId : siteId,
            description: values.description
        };
        const isNewHoliday = newHoliday.id <= 0;
        saveItem(setBusy, storeHoliday, newHoliday, isNewHoliday, setItemId, history);
    }

    const saveItem = useCallback((setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(item);
                toast.success(translate.byKey(TranslationKeys.holidaySaved));
                setRefreshKey(prevKey => prevKey + 1);  // Trigger a single refresh
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }, [translate, dialog, history]);

    return <TopicContextProvider dataMethod={getHolidayNamesWithPagination} topic={topic} itemId={itemId} selectedItem={selectedItem} >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveHoliday}
                tabs={tabs}
                initialValues={currentItem}
                itemPath={itemPath}
                children={
                    <HolidayDetails />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Zone;
